import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { OcrPrimeStore } from '../../store/ocr-prime.store';
import { AnchorTreeTrace, AnchorTreeTraceStep } from '../../types/document-metadata';

/**
 * Per-anchor breakdown shape returned by the backend `templates-matching.service`
 * (mirrors `AnchorMatchBreakdown` in `src/services/templates-matching.service.ts`).
 */
interface AnchorBreakdown {
  field: string;
  templateValue: string | null;
  documentValue: string | null;
  similarity: number;
  /** Configured minimum tolerance for this anchor (0–1), if any. */
  minTolerance?: number;
  /** True when this anchor caused the whole template to be rejected via minTolerance. */
  rejectedByMinTolerance?: boolean;
}

/**
 * Per-template ranking entry returned by the backend (mirrors `TemplateMatchResult`).
 */
interface TemplateMatch {
  templateId: string;
  score: number;
  matchedAnchors: number;
  totalAnchors: number;
  breakdown?: AnchorBreakdown[];
}

interface ParsedTemplateMismatchError {
  kind: 'template-mismatch';
  message: string;
  documentName?: string;
  /** Anchor-tree outcome embedded in the error payload (e.g. 'dead-end'). */
  outcome?: string;
  ranked: TemplateMatch[];
}

interface PlainParsedError {
  kind: 'plain';
  message: string;
}

type ParsedError = ParsedTemplateMismatchError | PlainParsedError;

/**
 * Renders an OCR processing error.
 *
 * If the error message is a JSON payload thrown by `selectBestTemplateForDocument`
 * for a "no matching template" case, displays a comparative table with each
 * candidate template's overall score and per-anchor similarity breakdown so
 * the user can understand why no template was confidently picked.
 *
 * Falls back to plain text rendering for any other error shape.
 */
@Component({
  selector: 'app-ocr-error',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterModule, MatIconModule, MatTooltipModule],
  template: `
    <div class="space-y-3 w-full">
    @if (parsed(); as p) {
      @if (p.kind === 'template-mismatch') {
        <div class="space-y-3 w-full">
          <p class="text-sm text-red-700 dark:text-red-300 font-medium">
            <mat-icon class="!text-base align-middle mr-1" aria-hidden="true">error_outline</mat-icon>
            {{ p.message }}
            @if (p.documentName) {
              <span class="ml-1 text-red-600 dark:text-red-400">— {{ p.documentName }}</span>
            }
          </p>

          @if (p.ranked.length > 0) {
            <div class="w-full overflow-auto border border-red-200 dark:border-red-800 rounded-md bg-white dark:bg-gray-800">
              <table class="w-full min-w-full text-xs">
                <thead class="bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200">
                  <tr>
                    <th class="px-2 py-1 text-left w-6"></th>
                    <th class="px-2 py-1 text-left">Template</th>
                    <th class="px-2 py-1 text-right">Score</th>
                    <th class="px-2 py-1 text-right">Matched</th>
                    <th class="px-2 py-1 text-right">Total anchors</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-red-100 dark:divide-red-900">
                  @for (row of p.ranked; track row.templateId; let i = $index) {
                    <tr class="hover:bg-red-50/50 dark:hover:bg-red-900/10"
                        [ngClass]="i === 0 ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''">
                      <td class="px-2 py-1 align-top">
                        <button type="button"
                                class="text-red-700 dark:text-red-300 hover:text-red-900 dark:hover:text-red-100"
                                (click)="toggle(row.templateId)"
                                [attr.aria-label]="isExpanded(row.templateId) ? 'Collapse breakdown' : 'Expand breakdown'">
                          <mat-icon class="!text-base align-middle">
                            {{ isExpanded(row.templateId) ? 'expand_more' : 'chevron_right' }}
                          </mat-icon>
                        </button>
                      </td>
                      <td class="px-2 py-1 align-top">
                        <a [routerLink]="['/templates', row.templateId]"
                           target="_blank"
                           class="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
                           [matTooltip]="row.templateId">
                          {{ templateName(row.templateId) }}
                          <mat-icon class="!text-sm !w-4 !h-4">open_in_new</mat-icon>
                        </a>
                      </td>
                      <td class="px-2 py-1 text-right align-top font-mono"
                          [class]="scoreClass(row.score)">
                        {{ percent(row.score) }}
                      </td>
                      <td class="px-2 py-1 text-right align-top text-gray-700 dark:text-gray-200">
                        {{ row.matchedAnchors }}
                      </td>
                      <td class="px-2 py-1 text-right align-top text-gray-700 dark:text-gray-200">
                        {{ row.totalAnchors }}
                      </td>
                    </tr>
                    @if (isExpanded(row.templateId) && row.breakdown && row.breakdown.length > 0) {
                      <tr>
                        <td></td>
                        <td colspan="4" class="px-2 py-2 bg-gray-50 dark:bg-gray-900/40">
                          <table class="min-w-full text-[11px]">
                            <thead class="text-gray-600 dark:text-gray-400">
                              <tr>
                                <th class="px-2 py-1 text-left">Anchor</th>
                                <th class="px-2 py-1 text-left">Template value</th>
                                <th class="px-2 py-1 text-left">Document value</th>
                                <th class="px-2 py-1 text-right">Similarity</th>
                                <th class="px-2 py-1 text-right" matTooltip="Configured minimum similarity for this anchor">Min tol.</th>
                              </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                              @for (b of row.breakdown; track b.field) {
                                <tr [ngClass]="b.rejectedByMinTolerance ? 'bg-red-50 dark:bg-red-900/20' : ''">
                                  <td class="px-2 py-1 font-medium text-gray-800 dark:text-gray-200">
                                    <div class="flex items-center gap-1.5">
                                      <span>{{ b.field }}</span>
                                      @if (b.rejectedByMinTolerance) {
                                        <span
                                          class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                          matTooltip="Similarity is below the configured minimum tolerance — template was rejected because of this anchor"
                                          data-testid="ocr-error-rejected-badge">
                                          <mat-icon class="!text-[12px] !w-3 !h-3">block</mat-icon>
                                          rejected
                                        </span>
                                      }
                                    </div>
                                  </td>
                                  <td class="px-2 py-1 text-gray-700 dark:text-gray-300 break-all">
                                    {{ b.templateValue ?? '—' }}
                                  </td>
                                  <td class="px-2 py-1 text-gray-700 dark:text-gray-300 break-all">
                                    {{ b.documentValue ?? '—' }}
                                  </td>
                                  <td class="px-2 py-1 text-right font-mono"
                                      [class]="b.rejectedByMinTolerance ? 'text-red-700 dark:text-red-400 font-semibold' : scoreClass(b.similarity)">
                                    {{ percent(b.similarity) }}
                                  </td>
                                  <td class="px-2 py-1 text-right font-mono text-gray-600 dark:text-gray-400">
                                    {{ b.minTolerance != null ? percent(b.minTolerance) : '—' }}
                                  </td>
                                </tr>
                              }
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    }
                  }
                </tbody>
              </table>
            </div>
          } @else {
            <p class="text-xs text-red-600 dark:text-red-400">
              No candidate templates were ranked.
            </p>
          }
        </div>
      } @else {
        <p class="text-sm text-red-600 dark:text-red-400 whitespace-pre-wrap">{{ p.message }}</p>
      }
    }

    <!-- Anchor-tree traversal history (template selection trace) -->
    @if (parsedTrace(); as t) {
      <div class="w-full border border-red-200 dark:border-red-800 rounded-md bg-white dark:bg-gray-800"
           data-testid="ocr-error-tree-trace">
        <button type="button"
                class="w-full flex items-center gap-2 px-3 py-2 text-left cursor-pointer hover:bg-red-50/50 dark:hover:bg-red-900/10"
                (click)="traceOpen.set(!traceOpen())"
                [attr.aria-expanded]="traceOpen()">
          <mat-icon class="!text-base !w-4 !h-4 text-red-700 dark:text-red-300">
            {{ traceOpen() ? 'expand_more' : 'chevron_right' }}
          </mat-icon>
          <span class="text-sm font-medium text-red-800 dark:text-red-200">Template selection trace</span>
          <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold"
                [class]="outcomeInfo(t.outcome).chipClass"
                data-testid="ocr-error-trace-outcome">
            {{ outcomeInfo(t.outcome).label }}
          </span>
        </button>

        @if (traceOpen()) {
          <div class="px-3 pb-3 space-y-3">
            <p class="text-xs text-gray-700 dark:text-gray-300">
              {{ outcomeInfo(t.outcome).description }}
            </p>

            <!-- Decision steps -->
            @if (t.steps.length > 0) {
              <div class="overflow-auto border border-gray-200 dark:border-gray-700 rounded-md">
                <table class="w-full min-w-full text-xs">
                  <thead class="bg-gray-50 dark:bg-gray-900/40 text-gray-600 dark:text-gray-400">
                    <tr>
                      <th class="px-2 py-1 text-left w-8">#</th>
                      <th class="px-2 py-1 text-left">Decisive anchor</th>
                      <th class="px-2 py-1 text-left">Extracted value</th>
                      <th class="px-2 py-1 text-left">Branch scores</th>
                      <th class="px-2 py-1 text-left">Result</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                    @for (step of t.steps; track step.nodeId; let i = $index) {
                      <tr [ngClass]="step.outcome === 'dead-end' ? 'bg-red-50 dark:bg-red-900/20' : ''">
                        <td class="px-2 py-1 align-top text-gray-500 dark:text-gray-400">{{ i + 1 }}</td>
                        <td class="px-2 py-1 align-top font-medium text-gray-800 dark:text-gray-200">
                          {{ step.decisiveAnchorFieldName }}
                        </td>
                        <td class="px-2 py-1 align-top text-gray-700 dark:text-gray-300 break-all">
                          {{ formatExtracted(step.extractedValue) }}
                        </td>
                        <td class="px-2 py-1 align-top">
                          <div class="flex flex-wrap gap-1">
                            @for (b of step.branchScores; track b.expectedValue) {
                              <span class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-mono border"
                                    [ngClass]="isChosen(step, b.expectedValue)
                                      ? 'bg-green-50 border-green-300 text-green-800 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                                      : 'bg-gray-50 border-gray-200 text-gray-600 dark:bg-gray-900/40 dark:border-gray-700 dark:text-gray-400'"
                                    [matTooltip]="isChosen(step, b.expectedValue) ? 'Branch followed' : 'Branch not followed'">
                                @if (isChosen(step, b.expectedValue)) {
                                  <mat-icon class="!text-[12px] !w-3 !h-3">arrow_forward</mat-icon>
                                }
                                {{ b.expectedValue }} · {{ percent(b.similarity) }}
                              </span>
                            }
                            @if (step.branchScores.length === 0) {
                              <span class="text-gray-500 dark:text-gray-400">—</span>
                            }
                          </div>
                        </td>
                        <td class="px-2 py-1 align-top">
                          @if (step.outcome === 'dead-end') {
                            <span class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                              <mat-icon class="!text-[12px] !w-3 !h-3">block</mat-icon>
                              dead end
                            </span>
                          } @else {
                            <span class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                              <mat-icon class="!text-[12px] !w-3 !h-3">check</mat-icon>
                              routed
                            </span>
                          }
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            } @else {
              <p class="text-xs text-gray-500 dark:text-gray-400">
                No decision steps were recorded — the tree routed directly to a leaf, or no tree traversal took place.
              </p>
            }

            <!-- Leaf anchors extracted from the document -->
            @if (leafAnchorEntries(t).length > 0) {
              <div>
                <p class="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Leaf anchors extracted from the document
                </p>
                <div class="overflow-auto border border-gray-200 dark:border-gray-700 rounded-md">
                  <table class="w-full min-w-full text-xs">
                    <thead class="bg-gray-50 dark:bg-gray-900/40 text-gray-600 dark:text-gray-400">
                      <tr>
                        <th class="px-2 py-1 text-left">Anchor</th>
                        <th class="px-2 py-1 text-left">Document value</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                      @for (entry of leafAnchorEntries(t); track entry.name) {
                        <tr>
                          <td class="px-2 py-1 font-medium text-gray-800 dark:text-gray-200">{{ entry.name }}</td>
                          <td class="px-2 py-1 text-gray-700 dark:text-gray-300 break-all">{{ entry.value }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            }
          </div>
        }
      </div>
    }
    </div>
  `,
})
export class OcrErrorComponent {
  /** Raw error message. Either a plain string or a JSON-encoded payload. */
  errorMessage = input<string | null | undefined>(null);

  /**
   * Anchor-tree traversal trace persisted on the document during auto
   * template-matching. Accepts the parsed object or its JSON-string form.
   */
  trace = input<AnchorTreeTrace | string | null | undefined>(null);

  private readonly store = inject(OcrPrimeStore);
  private readonly expanded = signal<Set<string>>(new Set());

  readonly traceOpen = signal(true);

  readonly parsed = computed<ParsedError | null>(() => {
    const raw = this.errorMessage();
    if (!raw) return null;
    return parseOcrError(raw);
  });

  readonly parsedTrace = computed<AnchorTreeTrace | null>(() => {
    const raw = this.trace();
    if (!raw) return null;
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw) as AnchorTreeTrace;
      } catch {
        return null;
      }
    }
    return raw;
  });

  outcomeInfo(outcome: AnchorTreeTrace['outcome'] | string | undefined): { label: string; description: string; chipClass: string } {
    switch (outcome) {
      case 'no-tree':
        return {
          label: 'no tree',
          description: 'No anchor tree is configured, so automatic template selection could not run. Author an anchor tree, or reprocess this document with a manually selected template.',
          chipClass: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
        };
      case 'dead-end':
        return {
          label: 'dead end',
          description: 'Traversal stopped before reaching a leaf: the value extracted from the document did not match any branch above its routing threshold. See the step marked "dead end" below for the mismatching anchor.',
          chipClass: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
        };
      case 'below-gate':
        return {
          label: 'below threshold',
          description: 'The tree routed the document to a leaf, but the best template score on that leaf was below the auto-select threshold — no template was confidently matched.',
          chipClass: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
        };
      case 'near-tie':
        return {
          label: 'near tie',
          description: 'Two templates scored too close together to safely pick one automatically. Review the candidates and reprocess with the correct template.',
          chipClass: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
        };
      case 'auto-selected':
        return {
          label: 'auto-selected',
          description: 'A template was selected automatically via the anchor tree.',
          chipClass: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        };
      default:
        return {
          label: String(outcome ?? 'unknown'),
          description: 'Anchor-tree traversal recorded during template selection.',
          chipClass: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
        };
    }
  }

  isChosen(step: AnchorTreeTraceStep, expectedValue: string): boolean {
    return step.chosenBranches.includes(expectedValue);
  }

  formatExtracted(value: unknown): string {
    if (value == null || value === '') return '—';
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  leafAnchorEntries(t: AnchorTreeTrace): { name: string; value: string }[] {
    const anchors = t.documentLeafAnchors;
    const names = t.leafAnchorFieldNames?.length
      ? t.leafAnchorFieldNames
      : Object.keys(anchors ?? {});
    return names.map(name => ({
      name,
      value: this.formatExtracted(anchors?.[name]),
    }));
  }

  templateName(id: string): string {
    return this.store.templatesMap()[id]?.name ?? id;
  }

  isExpanded(id: string): boolean {
    return this.expanded().has(id);
  }

  toggle(id: string): void {
    const next = new Set(this.expanded());
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    this.expanded.set(next);
  }

  percent(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) return '—';
    return `${(value * 100).toFixed(1)}%`;
  }

  scoreClass(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) return 'text-gray-500';
    if (value >= 0.8) return 'text-green-700 dark:text-green-400';
    if (value >= 0.5) return 'text-amber-700 dark:text-amber-400';
    return 'text-red-700 dark:text-red-400';
  }
}

/**
 * Parse an OCR error message string. Tries to detect the JSON payload thrown
 * by `selectBestTemplateForDocument` for the "no matching template" case.
 */
export function parseOcrError(raw: string): ParsedError {
  const trimmed = raw.trim();
  // Strip a leading "Error: " prefix that may be added by the FE pipeline.
  const candidate = trimmed.replace(/^Error:\s*/i, '');

  if (candidate.startsWith('{') || candidate.startsWith('[')) {
    try {
      const obj = JSON.parse(candidate);
      const ranked = extractRanked(obj);
      if (ranked || typeof obj?.error === 'string' || typeof obj?.outcome === 'string') {
        return {
          kind: 'template-mismatch',
          message: typeof obj?.error === 'string' ? obj.error : 'No matching template found',
          documentName: typeof obj?.documentName === 'string' ? obj.documentName : undefined,
          outcome: typeof obj?.outcome === 'string' ? obj.outcome : undefined,
          ranked: ranked ?? [],
        };
      }
    } catch {
      // fall through to plain
    }
  }

  return { kind: 'plain', message: raw };
}

function extractRanked(obj: unknown): TemplateMatch[] | null {
  if (!obj || typeof obj !== 'object') return null;
  const o = obj as Record<string, unknown>;
  const candidate = o['anchorMatch'] ?? o['ranked'] ?? null;
  if (!Array.isArray(candidate)) return null;
  const ranked: TemplateMatch[] = [];
  for (const entry of candidate) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    if (typeof e['templateId'] !== 'string') continue;
    ranked.push({
      templateId: e['templateId'] as string,
      score: typeof e['score'] === 'number' ? (e['score'] as number) : 0,
      matchedAnchors: typeof e['matchedAnchors'] === 'number' ? (e['matchedAnchors'] as number) : 0,
      totalAnchors: typeof e['totalAnchors'] === 'number' ? (e['totalAnchors'] as number) : 0,
      breakdown: Array.isArray(e['breakdown'])
        ? (e['breakdown'] as unknown[])
            .filter((b): b is Record<string, unknown> => !!b && typeof b === 'object')
            .map(b => ({
              field: typeof b['field'] === 'string' ? (b['field'] as string) : '',
              templateValue: normalizeNullable(b['templateValue']),
              documentValue: normalizeNullable(b['documentValue']),
              similarity: typeof b['similarity'] === 'number' ? (b['similarity'] as number) : 0,
              minTolerance: typeof b['minTolerance'] === 'number' ? (b['minTolerance'] as number) : undefined,
              rejectedByMinTolerance: b['rejectedByMinTolerance'] === true,
            }))
        : undefined,
    });
  }
  return ranked;
}

function normalizeNullable(v: unknown): string | null {
  if (v == null) return null;
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}
